import { useQuery } from '@tanstack/react-query';
import { Inbox, Newspaper, Briefcase, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatRelative } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

export function DashboardPage() {
  const { email, role } = useAuth();

  const { data: counts, isLoading } = useQuery({
    queryKey: ['dashboard-counts'],
    queryFn: async () => {
      const [projects, posts, team, contact] = await Promise.all([
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status_publish', 'published'),
        supabase.from('posts').select('*', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('team_members').select('*', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('contact_submissions').select('*', { count: 'exact', head: true }).eq('status', 'new'),
      ]);
      return {
        projects: projects.count ?? 0,
        posts: posts.count ?? 0,
        team: team.count ?? 0,
        newInbox: contact.count ?? 0,
      };
    },
  });

  const { data: recent } = useQuery({
    queryKey: ['dashboard-recent'],
    queryFn: async () => {
      const { data } = await supabase
        .from('contact_submissions')
        .select('id, name, email, topic, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Dashboard</p>
        <h1 className="mt-2 display-serif text-display-l text-fg">Welcome back, {email?.split('@')[0]}.</h1>
        <p className="mt-2 text-sm text-fg-muted">
          Signed in as <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-xs uppercase tracking-wider text-fg">{role}</span>
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading || !counts ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-20" />
                <Skeleton className="mt-2 h-8 w-16" />
              </CardHeader>
            </Card>
          ))
        ) : (
          <>
            <StatCard to="/projects" icon={Briefcase} label="Published projects" value={counts.projects} />
            <StatCard to="/blog" icon={Newspaper} label="Published posts" value={counts.posts} />
            <StatCard to="/team" icon={Users} label="Active team" value={counts.team} />
            <StatCard to="/inbox" icon={Inbox} label="New in inbox" value={counts.newInbox} />
          </>
        )}
      </div>

      <section>
        <header className="mb-4 flex items-end justify-between">
          <h2 className="text-h3 text-fg">Recent submissions</h2>
          <Link to="/inbox" className="text-xs text-gold-500 hover:underline">
            View all
          </Link>
        </header>
        <Card>
          {recent && recent.length > 0 ? (
            <table className="data-grid">
              <thead>
                <tr>
                  <th>When</th>
                  <th>From</th>
                  <th>Topic</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <tr key={row.id}>
                    <td className="text-fg-muted">{formatRelative(row.created_at)}</td>
                    <td>{row.name}</td>
                    <td>
                      <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-xs">{row.topic}</span>
                    </td>
                    <td className="text-fg-muted">{row.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center text-sm text-fg-subtle">No submissions yet.</div>
          )}
        </Card>
      </section>
    </div>
  );
}

interface StatCardProps {
  to: string;
  icon: typeof Inbox;
  label: string;
  value: number;
}

function StatCard({ to, icon: Icon, label, value }: StatCardProps) {
  return (
    <Link to={to} className="block">
      <Card className="transition-all hover:-translate-y-0.5 hover:border-gold-500/40 hover:shadow-gold">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardDescription>{label}</CardDescription>
            <Icon className="h-4 w-4 text-gold-500" />
          </div>
          <CardTitle className="text-3xl font-semibold">{value}</CardTitle>
        </CardHeader>
      </Card>
    </Link>
  );
}